import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/index.ts";
import {
  fetchIQWatchDevices,
  fetchIQWatchDevicesByOrganization,
  fetchAllIQWatchDevices, // Add this import for fetching all organizations
  setSearchTerm, // Add this import
  DeviceFilter, // Add this import
} from "../store/dashboardSlice.ts";
import { Site, Organization, IQWatchDeviceWithData } from "../types";
import DashboardTabs from "../components/dashboard/DashboardTabs.tsx";
import RoutersTable from "../components/dashboard/RoutersTable.tsx";
// MPPTDeviceList import removed - focusing on IQWatch devices only
// EdgeDeviceList import removed - focusing on IQWatch devices only
import DashboardIQWatchDeviceList from "../components/dashboard/DashboardIQWatchDeviceList.tsx";
import IQWatchDeviceDetail from "../components/dashboard/IQWatchDeviceDetail.tsx";
import { useOutletContext } from "react-router-dom";
// getDevicesLatestLogs import removed - focusing on IQWatch devices only
// OverviewSummaryCard import removed - focusing on IQWatch devices only
// calculateBatteryPercentage import removed - focusing on IQWatch devices only
import DashboardViewToggle, {
  DashboardView,
} from "../components/dashboard/DashboardViewToggle.tsx";
import DashboardMapView from "../components/dashboard/DashboardMapView.tsx";

interface DashboardContext {
  selectedSite: Site | null;
  selectedOrganization: Organization | null; // Add this
  organizations: Organization[];
  isAllSitesSelected: boolean; // Add this line
}

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    selectedSite,
    selectedOrganization,
    organizations,
    isAllSitesSelected,
  } = useOutletContext<DashboardContext>();

  // 添加 activeTab 状态管理
  const [activeTab, setActiveTab] = useState<"routers" | "iqwatch-devices">(
    "iqwatch-devices"
  ); // Default to iqwatch-devices since MPPT removed

  // Add view state management
  const [dashboardView, setDashboardView] = useState<DashboardView>("list");

  // State to store all devices for map view (unpaginated)
  const [mapViewDevices, setMapViewDevices] = useState<IQWatchDeviceWithData[]>([]);
  const [mapViewLoading, setMapViewLoading] = useState(false);

  // State for device selected from map view
  const [selectedDeviceFromMap, setSelectedDeviceFromMap] =
    useState<IQWatchDeviceWithData | null>(null);
  const [initialTab, setInitialTab] = useState<
    "battery" | "router" | "cameras" | undefined
  >(undefined);

  // State to track if device detail is being shown (from table selection)
  const [isDeviceDetailView, setIsDeviceDetailView] = useState(false);

  // Local state for search input (to allow typing without triggering search)
  const [searchInput, setSearchInput] = useState<string>("");

  // 从Redux store获取数据
  const {
    // mpptDevices removed - focusing on IQWatch devices only
    iqwatchDevices,
    iqwatchPagination, // Add this new field
    iqwatchSummary, // Add summary statistics
    sortField,
    sortOrder,
    searchTerm,
    deviceFilter,
    loading,
    error,
    // lastFetchTime removed - not used
  } = useSelector((state: RootState) => state.dashboard);

  // 使用ref来跟踪状态，避免无限循环
  // isInitializedRef and lastFetchTimeRef removed - not used
  const currentSiteRef = useRef<string | null>(null);
  const currentOrgRef = useRef<string | null>(null);

  // 获取设备ID列表
  const deviceIds = useMemo(() => {
    if (!selectedSite) return [];

    // Edge device IDs removed - focusing on IQWatch devices only
    return [];
  }, [selectedSite]);

  // 获取当前组织ID
  const currentOrgId = useMemo(() => {
    // If an organization is selected (All Sites mode), use its ID
    if (selectedOrganization) {
      return selectedOrganization.id;
    }

    // Otherwise, find the organization that contains the selected site
    if (!selectedSite || !organizations.length) return null;

    return organizations.find(
      (org) =>
        org.sites.some((site) => site.id === selectedSite.id) ||
        org.sites.some((site) => {
          const findSiteInChildren = (
            sites: Site[],
            targetId: string
          ): boolean => {
            return sites.some((site) => {
              if (site.id === targetId) return true;
              if (site.children)
                return findSiteInChildren(site.children, targetId);
              return false;
            });
          };
          return findSiteInChildren(org.sites, selectedSite.id);
        })
    )?.id;
  }, [selectedSite, selectedOrganization, organizations]);

  // fetchDevicesDataCallback removed - focusing on IQWatch devices only
  const fetchDevicesDataCallback = useCallback(() => {
    // No Edge device data fetching needed
  }, []);

  // 修复：使用稳定的依赖项 - 更新以支持组织级别的设备获取、排序、搜索和过滤
  const fetchIQWatchDevicesCallback = useCallback(
    (
      page: number = 1,
      searchOverride?: string,
      filterOverride?: DeviceFilter
    ) => {
      // Do not clear devices before fetch: clearing + global loading spinner unmounted
      // the device list and dropped in-detail state (e.g. opening the Cameras tab).
      // Fulfilled handlers replace list data when the request completes.

      // Use searchOverride if provided, otherwise use searchTerm from Redux
      const searchToUse =
        searchOverride !== undefined ? searchOverride : searchTerm;

      // Use filterOverride if provided, otherwise use deviceFilter from Redux
      const filterToUse =
        filterOverride !== undefined ? filterOverride : deviceFilter;

      // All Organizations mode - when no specific organization is selected
      if (!selectedOrganization && !selectedSite) {
        dispatch(
          fetchAllIQWatchDevices({
            page,
            limit: 10,
            sortBy: sortField || undefined,
            sortOrder: sortOrder || undefined,
            search: searchToUse || undefined,
            filter: filterToUse !== "all" ? filterToUse : undefined,
          })
        );
        return;
      }

      if (currentOrgId) {
        if (isAllSitesSelected && selectedOrganization) {
          // All Sites mode - fetch all devices from organization
          dispatch(
            fetchIQWatchDevicesByOrganization({
              orgId: selectedOrganization.id,
              page,
              limit: 10,
              sortBy: sortField || undefined,
              sortOrder: sortOrder || undefined,
              search: searchToUse || undefined,
              filter: filterToUse !== "all" ? filterToUse : undefined,
            })
          );
        } else if (selectedSite?.id) {
          // Single site mode - fetch devices for specific site
          dispatch(
            fetchIQWatchDevices({
              siteId: selectedSite.id,
              orgId: currentOrgId,
              page,
              limit: 10, // Default to 10 devices per page
              sortBy: sortField || undefined,
              sortOrder: sortOrder || undefined,
              search: searchToUse || undefined,
              filter: filterToUse !== "all" ? filterToUse : undefined,
            })
          );
        }
      }
    },
    [
      selectedSite,
      selectedOrganization,
      currentOrgId,
      isAllSitesSelected,
      sortField,
      sortOrder,
      searchTerm,
      deviceFilter,
      dispatch,
    ]
  );

  // Add a new callback for handling page changes:
  const handleIQWatchPageChange = useCallback(
    (page: number) => {
      fetchIQWatchDevicesCallback(page);
    },
    [fetchIQWatchDevicesCallback]
  );

  // 初始化数据获取 - 只在site或orgId变化时执行
  useEffect(() => {
    const siteId = selectedSite?.id;
    const orgId = currentOrgId;

    // 检查是否有实际变化
    if (siteId !== currentSiteRef.current || orgId !== currentOrgRef.current) {
      currentSiteRef.current = siteId || null;
      currentOrgRef.current = orgId || null;

      if (deviceIds.length > 0) {
        fetchDevicesDataCallback();
      }

      // Fetch iQWatch devices - including "All Organizations" mode
      // If neither organization nor site is selected, fetch all devices from all organizations
      // If we have an organization, fetch devices for that organization (specific site or all sites)
      fetchIQWatchDevicesCallback(1); // Reset to page 1 when site/org changes
    }
  }, [
    selectedSite?.id,
    selectedOrganization?.id,
    currentOrgId,
    deviceIds,
    fetchDevicesDataCallback,
    fetchIQWatchDevicesCallback,
  ]);

  // Refetch when sort changes - reset to page 1
  useEffect(() => {
    if (sortField && sortOrder) {
      fetchIQWatchDevicesCallback(1);
    }
  }, [sortField, sortOrder, fetchIQWatchDevicesCallback]);

  // Refetch when device filter changes - reset to page 1
  useEffect(() => {
    fetchIQWatchDevicesCallback(1);
  }, [deviceFilter, fetchIQWatchDevicesCallback]);

  // Fetch all devices for map view when switching to map view
  useEffect(() => {
    if (dashboardView === "map") {
      setMapViewLoading(true);
      
      // Fetch all devices with a high limit for map view
      const fetchAllDevicesForMap = async () => {
        try {
          // All Organizations mode - when no specific organization is selected
          if (!selectedOrganization && !selectedSite) {
            const result = await dispatch(
              fetchAllIQWatchDevices({
                page: 1,
                limit: 100, // Maximum allowed limit for map view
                sortBy: sortField || undefined,
                sortOrder: sortOrder || undefined,
                search: undefined, // Don't apply search for map view
                filter: undefined, // Don't apply filter for map view
              })
            );
            if (result.type.endsWith("/fulfilled")) {
              const payload = result.payload as {
                devices: IQWatchDeviceWithData[];
                pagination: any;
                summary: any;
              };
              setMapViewDevices(payload.devices || []);
            }
            setMapViewLoading(false);
            return;
          }

          if (currentOrgId) {
            if (isAllSitesSelected && selectedOrganization) {
              // All Sites mode - fetch all devices from organization
              const result = await dispatch(
                fetchIQWatchDevicesByOrganization({
                  orgId: selectedOrganization.id,
                  page: 1,
                  limit: 100, // Maximum allowed limit for map view
                  sortBy: sortField || undefined,
                  sortOrder: sortOrder || undefined,
                  search: undefined, // Don't apply search for map view
                  filter: undefined, // Don't apply filter for map view
                })
              );
              if (result.type.endsWith("/fulfilled")) {
                const payload = result.payload as {
                  devices: IQWatchDeviceWithData[];
                  pagination: any;
                  summary: any;
                };
                setMapViewDevices(payload.devices || []);
              }
              setMapViewLoading(false);
            } else if (selectedSite?.id) {
              // Single site mode - fetch devices for specific site
              const result = await dispatch(
                fetchIQWatchDevices({
                  siteId: selectedSite.id,
                  orgId: currentOrgId,
                  page: 1,
                  limit: 100, // Maximum allowed limit for map view
                  sortBy: sortField || undefined,
                  sortOrder: sortOrder || undefined,
                  search: undefined, // Don't apply search for map view
                  filter: undefined, // Don't apply filter for map view
                })
              );
              if (result.type.endsWith("/fulfilled")) {
                const payload = result.payload as {
                  devices: IQWatchDeviceWithData[];
                  pagination: any;
                  summary: any;
                };
                setMapViewDevices(payload.devices || []);
              }
              setMapViewLoading(false);
            } else {
              setMapViewLoading(false);
            }
          } else {
            setMapViewLoading(false);
          }
        } catch (error) {
          console.error("Error fetching devices for map view:", error);
          setMapViewLoading(false);
        }
      };

      fetchAllDevicesForMap();
    } else {
      // Clear map view devices when switching away from map view
      setMapViewDevices([]);
    }
  }, [
    dashboardView,
    selectedSite,
    selectedOrganization,
    currentOrgId,
    isAllSitesSelected,
    sortField,
    sortOrder,
    dispatch,
  ]);

  // Handle search - only trigger when user clicks search button or presses Enter
  const handleSearch = useCallback(() => {
    const trimmedSearch = searchInput.trim();
    dispatch(setSearchTerm(trimmedSearch));
    // Pass search term directly to avoid Redux state timing issues
    fetchIQWatchDevicesCallback(1, trimmedSearch);
  }, [searchInput, dispatch, fetchIQWatchDevicesCallback]);

  // Sync local search input with Redux search term when it changes externally
  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput("");
    dispatch(setSearchTerm(""));
    // Pass empty string explicitly to ensure we fetch all devices
    fetchIQWatchDevicesCallback(1, "");
  };

  // 移除定期刷新，避免无限循环
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (deviceIds.length > 0) {
  //       fetchDevicesDataCallback();
  //     }
  //   }, 60000);
  //   return () => clearInterval(interval);
  // }, [fetchDevicesDataCallback]);

  // filteredMPPTDevices removed - focusing on IQWatch devices only

  // filteredDeviceLogs removed - focusing on IQWatch devices only
  // filteredDeviceLogs removed - focusing on IQWatch devices only

  // hasEdgeComputerDevices removed - focusing on IQWatch devices only

  // allRouters removed - focusing on IQWatch devices only
  const allRouters = useMemo(() => {
    return [];
  }, []);

  // hasMPPTDevices removed - focusing on IQWatch devices only

  const hasRouters = useMemo(() => {
    return allRouters.length > 0;
  }, [allRouters]);

  // hasEdgeDevices removed - focusing on IQWatch devices only

  const hasIQWatchDevices = useMemo(() => {
    return iqwatchDevices.length > 0;
  }, [iqwatchDevices]);

  // Edge Computer device switching logic removed - focusing on IQWatch devices only

  // 只在初始化时自动选择第一个有数据的tab，避免干扰用户手动选择
  useEffect(() => {
    // 检查当前选中的tab是否有数据，如果没有数据则自动切换到有数据的tab
    const currentTabHasData =
      (activeTab === "routers" && hasRouters) ||
      (activeTab === "iqwatch-devices" && hasIQWatchDevices);

    if (!currentTabHasData) {
      // 按优先级选择第一个有数据的tab
      if (hasRouters) {
        setActiveTab("routers");
      } else if (hasIQWatchDevices) {
        setActiveTab("iqwatch-devices");
      }
    }
  }, [hasRouters, hasIQWatchDevices, activeTab]); // Add activeTab back to dependencies

  // 检查是否只有iQWatch设备（没有其他类型设备）
  const onlyHasIQWatchDevices = useMemo(() => {
    return hasIQWatchDevices && !hasRouters;
  }, [hasIQWatchDevices, hasRouters]);

  // Handle browser back button for device detail (must be before early returns)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // When browser back button is pressed, close the device detail
      if (selectedDeviceFromMap) {
        setSelectedDeviceFromMap(null);
        setInitialTab(undefined);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [selectedDeviceFromMap]);

  // Handler to go back from device detail
  const handleBackFromDeviceDetail = () => {
    window.history.back();
  };

  // Full-page loader only when we have no list data yet. Refetches keep the list
  // (and device detail) mounted so tab navigation is not reset.
  if (loading.iqwatchDevices && iqwatchDevices.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading devices...</p>
        </div>
      </div>
    );
  }

  // Determine if we're in "All Organizations" mode
  const isAllOrganizationsMode = !selectedSite && !selectedOrganization;

  // Don't show the empty state anymore - we'll show data from all organizations

  // Show device detail if selected from map
  if (selectedDeviceFromMap) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="p-2">
          <IQWatchDeviceDetail
            deviceWithData={selectedDeviceFromMap}
            onBack={handleBackFromDeviceDetail}
            initialTab={initialTab}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Remove DashboardHeader */}
      {/* <DashboardHeader isAllDevices={isAllDevices} selectedDevice={null} /> */}
      {/* Remove the spacing div as well */}
      {/* <div className="mb-3" /> */}
      <>
        {/* View Toggle - Show when we have data to display */}
        {(hasRouters || hasIQWatchDevices) && (
          <>
            <div className="flex items-center justify-between mb-4 ml-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedSite
                  ? `${selectedSite.name} Dashboard`
                  : selectedOrganization
                  ? `${selectedOrganization.name} - All Sites Dashboard`
                  : "All Customers Dashboard"}
              </h2>
              <DashboardViewToggle
                activeView={dashboardView}
                onViewChange={setDashboardView}
              />
            </div>
          </>
        )}

        {/* Map View */}
        {dashboardView === "map" && (
          <DashboardMapView
            organizations={organizations}
            selectedSite={selectedSite}
            selectedOrganization={selectedOrganization || undefined} // Convert null to undefined
            // mpptDevices prop removed - focusing on IQWatch devices only
            // deviceLogs prop removed - focusing on IQWatch devices only
            iqwatchDevices={mapViewDevices.length > 0 ? mapViewDevices : iqwatchDevices}
            // deviceDetails prop removed - focusing on IQWatch devices only
            loading={mapViewLoading || loading.iqwatchDevices}
            error={error || undefined} // Convert null to undefined
            onSiteSelect={(site) => {
              // You can implement site selection logic here if needed
            }}
            onDeviceClick={(device, tab) => {
              // Switch to list view and show device detail
              setSelectedDeviceFromMap(device);
              setInitialTab(tab);
              setDashboardView("list");
              // Push state to browser history for back button support
              window.history.pushState(
                { deviceId: device.device.id, view: "deviceDetail" },
                "",
                window.location.href
              );
            }}
          />
        )}

        {/* List View - Original dashboard content */}
        {dashboardView === "list" && (
          <>
            {/* Search Field - Show when in valid context (site/organization selected) and not in device detail view */}
            {(isAllOrganizationsMode || selectedOrganization || selectedSite) &&
              !isDeviceDetailView &&
              !selectedDeviceFromMap && (
                <div className="mb-4 ml-6 mr-6">
                  <div className="flex items-center gap-2 max-w-md">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Search device name..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                        className="w-full px-3 py-1.5 pl-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                      <svg
                        className="absolute left-2 top-1.5 h-4 w-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <button
                      onClick={handleSearch}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Search
                    </button>
                    {searchTerm && (
                      <button
                        onClick={handleClearSearch}
                        className="px-2 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Clear search"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

            {/* 只在不是纯iQWatch设备时显示标签页 */}
            {!onlyHasIQWatchDevices && (
              <>
                <DashboardTabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  routersCount={allRouters.length}
                  iqwatchDevicesCount={iqwatchDevices.length}
                  hasRouters={hasRouters}
                  hasIQWatchDevices={hasIQWatchDevices}
                />
                <div className="mb-6" />
              </>
            )}

            {/* iQWatch Devices - Always show when in correct mode, even if no devices (to keep cards visible) */}
            {/* Show when: (only IQWatch devices mode) OR (on iqwatch-devices tab) */}
            {/* And when: (in All Organizations mode) OR (have selected organization) OR (have selected site) */}
            {(onlyHasIQWatchDevices || activeTab === "iqwatch-devices") &&
              (isAllOrganizationsMode ||
                selectedOrganization ||
                selectedSite) && (
                <DashboardIQWatchDeviceList
                  site={selectedSite}
                  organization={selectedOrganization || undefined} // Convert null to undefined
                  orgId={currentOrgId || undefined} // Convert null to undefined
                  iqwatchDevices={iqwatchDevices}
                  loading={loading.iqwatchDevices}
                  error={error} // Keep as is (string | null)
                  pagination={iqwatchPagination || undefined} // Convert null to undefined
                  summary={iqwatchSummary || undefined} // Pass summary statistics
                  searchTerm={searchTerm} // Pass search term
                  onPageChange={handleIQWatchPageChange}
                  isAllOrganizationsMode={isAllOrganizationsMode} // Pass the flag
                  onDeviceSelect={() => setIsDeviceDetailView(true)}
                  onDeviceDeselect={() => setIsDeviceDetailView(false)}
                />
              )}

            {/* Routers Tab - 只在选中且有数据时显示 */}
            {activeTab === "routers" && hasRouters && (
              <RoutersTable
                deviceLogs={allRouters}
                getSignalQuality={(rssi: number) => {
                  if (rssi >= -50)
                    return { quality: "Excellent", color: "text-green-600" };
                  if (rssi >= -60)
                    return { quality: "Good", color: "text-green-500" };
                  if (rssi >= -70)
                    return { quality: "Fair", color: "text-yellow-600" };
                  return { quality: "Poor", color: "text-red-600" };
                }}
              />
            )}

            {/* 当所有设备类型都没有数据时显示空状态 - 只在没有选择任何上下文时显示 */}
            {/* Don't show this empty state if we're in a mode where DashboardIQWatchDeviceList should be shown */}
            {!hasIQWatchDevices &&
              !hasRouters &&
              !isAllOrganizationsMode &&
              !selectedOrganization &&
              !selectedSite && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="mb-6">
                      <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-12 h-12 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No devices found
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Please select a customer, site, or "All Customers" to
                        view devices.
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </>
        )}
      </>
    </div>
  );
};

export default Dashboard;
